/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {MarketplaceProductApiService} from '../admin/marketplace-product/marketplace-product-api.service';
import {LibraryCreativeApiService} from '../library-creative/library-creative-api.service';
import _ from 'lodash';
import {MEDIA_TYPE, MediaTypeHelper} from '../../components/util/media-type-helper';
import {DEFAULT_LAYOUT_TYPES} from './creative-helper.service';
import {CreativeRetailProductModalService} from './creative-retail-product-modal.service';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {ASPECT_RATIOS_TRANSLATIONS} from '../admin/layout/layout-commons';
import {resolveCreativePreviewAdvertiser} from './ad/preview-request-data';
import {requiresVideoPreviewParam} from './ad/preview-language';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {LocalBulkCreativeInfo} from '../../components/session/local-bulk-creative-info';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {formatMediaTypeKey, formatMediaTypesTranslate} from '../common/filters/format-media-type';
import {attachEditPagePhase} from '../common/util/edit-page-phase';
import type {EditPagePhaseApi} from '../common/util/edit-page-phase';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {findCreativeAdvertiserMismatch} from './creative-advertiser-mismatch';
import {TeamApiService} from '../admin/team/team-api.service';
import {ProductFeedApiService} from '../common/controller/product-feed-api.service';
import {ExternalDemandSourceApiService} from '../admin/external-demand-source/external-demand-source-api.service';
import {CreativeApiService} from './creative-api.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {Injectable} from '@angular/core';
import type {CreativeEditPageComponent} from './creative-edit-page.component';

export interface CreativeEditSetupParams {
  model: any;
  readOnly: boolean;
  creativeType: any;
  lineItemInfo: any;
  $state: any;
  navigationHistory: NavigationHistoryService;
  routeParams: any;
  translate: any;
  teamApi: TeamApiService;
  productFeedApi: ProductFeedApiService;
  CreativeHelper: any;
  Rollbar: any;
  Asset: any;
  Creative: CreativeApiService;
  creativeApi: CreativeApiService;
  LibraryCreative: LibraryCreativeApiService;
  lineItemApi: LineItemApiService;
  MarketplaceProduct: MarketplaceProductApiService;
  externalDemandSourceApi: ExternalDemandSourceApiService;
  LocalNetworkProfile: any;
  LocalUserPermissions: any;
  Advertiser: any;
  TargetingEditorModalService: any;
  creativeRetailProductModalService: CreativeRetailProductModalService;
  localNetworkInfo: LocalNetworkInfo;
}

export interface CreativeEditBehavior {
  afterInit(): void;
  afterInitAfterSave(): void;
  afterSave(creative: any): void;
  beforeSubmit(): void;
  destroy(): void;
}

@Injectable({providedIn: 'root'})
export class CreativeEditBehaviorService {
  initialize(page: CreativeEditPageComponent, params: CreativeEditSetupParams): CreativeEditBehavior {
    return configureCreativeEdit(page, params);
  }
}

function configureCreativeEdit(page: CreativeEditPageComponent, p: CreativeEditSetupParams): CreativeEditBehavior {
  const {
    model, readOnly, creativeType, lineItemInfo, $state, navigationHistory, routeParams, translate,
    teamApi, productFeedApi, CreativeHelper, Rollbar, Asset, Creative, creativeApi,
    LibraryCreative, lineItemApi,
    MarketplaceProduct, externalDemandSourceApi, LocalNetworkProfile, LocalUserPermissions, Advertiser,
    TargetingEditorModalService, creativeRetailProductModalService, localNetworkInfo
  } = p;

  const editPhase: EditPagePhaseApi = attachEditPagePhase(page);
  const mInitPromises: any[] = [];
  const mLayoutAssetMatches: Record<string, any> = {};
  const defaultLayoutType = 'OTHER';
  const prevAssetAssignments: Record<string, any> = {};
  let mAllLayoutsPromise: any;
  let mLineItemPromise: any;
  let mPreviewAdvertiserPromise: any;
  let mPreviewAdvertiserId: any;
  let suppressLineItemTransitionOnce = false;
  let marketplaceProduct: any = null;
  let timeoutPromise: any;
  let codeMirrorRefreshTimeout: ReturnType<typeof setTimeout> | undefined;
  let teamQuery: any;

  const reviewWorkflowTypes = ['PROPOSED', 'CREATIVE_ONLY'];

  page.editsMade = () => page.markExternalFormDirty();
  page.updateModelHappened = () => page.publishModelUpdate();

  const syncEditingModel = () => undefined;

  const updateWorkflowRestrictions = (lineItem: any, workflowType: string) => {
    if (!page.isSelfService) {
      return;
    }
    const isLockedDown = lineItem.userState !== 'PROPOSED' && workflowType === 'PROPOSED';
    const isCreativeContentLocked = lineItem.userState !== 'PROPOSED' && reviewWorkflowTypes.indexOf(workflowType) > -1;
    page.patchCreativeViewState({isLockedDown, isCreativeContentLocked});
    page.blockSubmit = page.blockSubmit || (page.isNew && isCreativeContentLocked);
  };

  page.hasExternalSources = localNetworkInfo.getInfo('externalDemandSourceCount') > 0;
  page.retailProductFields = {
    retailProductId: true,
    merchantId: true,
    title: true,
    body: true,
    image: true,
    clickUrl: true,
    sponsoredBy: true,
    priceCurrency: true,
    priceAmount: true,
    salePriceCurrency: true,
    salePriceAmount: true
  };

  page.aspectRatioDescriptions = ASPECT_RATIOS_TRANSLATIONS;
  page.layoutRef = {};
  page.skips = ['validation.warning.creative.blocked.by.scanner'];
  page.isPureMarketplace = LocalNetworkProfile.hasAdvertiserEnforced();
  page.lineItemParent = lineItemInfo.fromLineItem || _.get(model, ['lineItem', 'id']);
  page.creativeSetParent = lineItemInfo.fromCreativeSet || _.get(model, ['creativeSet', 'id']);
  page.useCookies = true;
  if (page.creativeSetParent && _.get(model, ['creativeSet', 'id'])) {
    lineItemInfo.id = lineItemInfo.id || _.get(model, ['creativeSet', 'id']);
    lineItemInfo.name = lineItemInfo.name || _.get(model, ['creativeSet', 'id']);
  }

  const doBulkUpdates = (localCreatives: any) => {
    if (page.isBulkCreative) {
      return;
    }
    page.bulkCreatives = localCreatives;
    page.isBulkCreative = _.find(page.bulkCreatives, (c: any) => c.id === model.id);
  };

  doBulkUpdates(LocalBulkCreativeInfo.obtain().getCreatives());
  doBulkUpdates(LocalBulkCreativeInfo.obtain().getLibraryCreatives());

  page.defaultLayoutTypes = DEFAULT_LAYOUT_TYPES;
  page.layoutTypes = page.defaultLayoutTypes.concat(defaultLayoutType);

  page.isSelfService = LocalUserPermissions.isSelfService();
  page.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser() && !(page.creativeSetParent || page.fromCreativeSet);
  page.needsVerificationCheck = LocalNetworkProfile.isVerificationCheckNetwork();
  page.isCreative = creativeType.type === 'Creative';
  page.perms = LocalUserPermissions.getAllVisPermissions();
  page.creativeFields = ['name', {param: 'Layout', label: 'layout'}, {param: 'LineItem', label: 'lineItem'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];
  page.linkedCreativeFields = ['name', {param: 'LineItem', label: 'lineItem'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];

  if (!page.isCreative) {
    const params: any = {minimal: true};
    if (page.isPureMarketplace) {
      params.filterBy = 'type';
      params.filterByValue = 'AX_ADVERTISER';
    }
    teamQuery = firstValueFrom(teamApi.listOptions(params)).then((teamPage) => {
      page.allTeams = teamPage.results;
    });
  }

  const delayedPreview = () => {
    clearTimeout(timeoutPromise);
    timeoutPromise = setTimeout(() => callForPreview(), 1500);
  };

  const updateDefaultName = (assignedAsset?: any) => {
    CreativeHelper.updateDefaultName(page, false, assignedAsset);
  };

  const hydrateSelectedRetailProduct = (productId: string, merchantId: string) => {
    if (!productId || !merchantId) {
      page.selRetailProduct = null;
      return Promise.resolve(null);
    }
    return firstValueFrom(productFeedApi.get(productId, merchantId)).then((retailProd: any) => {
      page.merchantGuide = localNetworkInfo.getMerchantGuide();
      retailProd.hydratedMerchant = page.merchantGuide[retailProd.merchantId];
      page.selRetailProduct = retailProd;
      return retailProd;
    });
  };

  const applyRetailCreativeUpdate = (creative: any) => {
    syncEditingModel();
    page.editingModel = {...creative};
    const hydratedLayout = page.layoutRef[_.get(page.editingModel, ['layout', 'id'])];
    if (hydratedLayout) {
      page.editingModel.layout = hydratedLayout;
    }
    suppressLineItemTransitionOnce = true;
    page.applyBehaviorSave(creative);
  };

  const ensureRetailCreativeIsSaved = () => {
    if (!page.isNew) {
      return Promise.resolve(page.editingModel);
    }
    page.prepareCreativeForSubmit();
    return firstValueFrom(creativeApi.save(page.editingModel)).then((savedCreative: any) => {
      page.editingModel.id = savedCreative.id;
      _.set(page.editingModel, 'validationWarnings', savedCreative.validationWarnings);
      return savedCreative;
    });
  };

  const getCurrentPreviewAdvertiser = () => {
    const modelAdvertiserId = _.get(page.editingModel, ['advertiser', 'id']);
    const lineItemAdvertiserId = _.get(page.lineItem, ['advertiser', 'id']);
    if (_.get(page.advertiserInfo, 'id') && (_.get(page.advertiserInfo, 'id') === modelAdvertiserId || _.get(page.advertiserInfo, 'id') === lineItemAdvertiserId)) {
      return page.advertiserInfo;
    }
    return page.editingModel.advertiser || _.get(page.lineItem, 'advertiser') || page.advertiserInfo || null;
  };

  const getAdvertiserForPreview = () => {
    const advertiser = getCurrentPreviewAdvertiser();
    return Promise.resolve(resolveCreativePreviewAdvertiser(page.editingModel, page.lineItem, advertiser, (advertiserId: string) => {
      if (_.get(page.advertiserInfo, 'id') === advertiserId && _.get(page.advertiserInfo, 'legalName')) {
        return page.advertiserInfo;
      }
      if (mPreviewAdvertiserPromise && mPreviewAdvertiserId === advertiserId) {
        return mPreviewAdvertiserPromise;
      }
      mPreviewAdvertiserId = advertiserId;
      mPreviewAdvertiserPromise = firstValueFrom(Advertiser.get(advertiserId)).then((data: any) => {
        page.advertiserInfo = data;
        return data;
      }, () => advertiser);
      return mPreviewAdvertiserPromise;
    }));
  };

  const updateCreativeDims = () => {
    syncEditingModel();
    CreativeHelper.updateCreativeDims(page, prevAssetAssignments);
  };

  const getLineItemInfo = () => {
    marketplaceProduct = null;
    page.patchCreativeViewState({
      optionalTargetTypes: [],
      hasAxProductDimensions: false,
      allowedDimensions: [],
      selectedDimension: undefined
    });
    mLineItemPromise = firstValueFrom(lineItemApi.get(page.editingModel.lineItem.id)).then((lineItem: any) => {
      page.lineItem = lineItem;
      page.lineItemInfo = lineItem;
      updateWorkflowRestrictions(lineItem, _.get(lineItem, ['axProduct', 'lineItemWorkflowType'], 'DEFAULT'));
      page.chartCurrency = _.get(lineItem, ['bidSpecification', 'cpm', 'currency'])
        || _.get(lineItem, ['bidSpecification', 'cpc', 'currency'])
        || _.get(lineItem, ['objectives', 'BUDGET', 'currency']);
      if (_.get(page.lineItem, ['axProduct', 'id'])) {
        return firstValueFrom(MarketplaceProduct.get(lineItem.axProduct.id)).then((mp: any) => {
          marketplaceProduct = mp;
          const allowedDimensions = (mp.allowedDimensions || []).map((allowedDimension: any) => {
            return {
              label: allowedDimension[0] + ' x ' + allowedDimension[1],
              width: allowedDimension[0],
              height: allowedDimension[1]
            };
          });
          updateWorkflowRestrictions(lineItem, mp.lineItemWorkflowType || 'DEFAULT');
          const selectedDimension = allowedDimensions.find((dimension: any) => page.editingModel.width === dimension.width && page.editingModel.height === dimension.height);
          page.patchCreativeViewState({
            optionalTargetTypes: _.isArray(mp.optionalTargetTypes) ? mp.optionalTargetTypes : [],
            hasAxProductDimensions: allowedDimensions.length > 0,
            allowedDimensions,
            selectedDimension
          });
          if (allowedDimensions.length > 0) {
            page.editingModel.dimensionType = 'FIXED';
          }
          return Promise.resolve(mAllLayoutsPromise).then(() => {
            page.layouts = _.filter(page.layouts, (lay: any) => {
              return _.get(page.editingModel, ['layout', 'id']) === lay.id || _.find(mp.layouts, (l: any) => l.id === lay.id);
            });
            return lineItem;
          });
        }).catch((error: any) => {
          Rollbar.error('Creative marketplace product hydration problem', error);
          return lineItem;
        });
      }
      return lineItem;
    }).catch((error: any) => {
      Rollbar.error('Creative line item hydration problem', error);
      return undefined;
    });
  };

  const checkHtmlUrls = () => {
    syncEditingModel();
    CreativeHelper.checkHtmlUrls(page);
  };

  const matchConstraintsAndAssets = (newAssetUpload?: boolean) => {
    syncEditingModel();
    return CreativeHelper.matchConstraintsAndAssets(page, mInitPromises, mLayoutAssetMatches, prevAssetAssignments, delayedPreview, newAssetUpload);
  };

  const updateEmailCode = () => {
    page.emailCodeReason = null;
    const destUrl = _.find(page.editingModel.cToUrls, (v: any) => !!v) || '';
    if (!destUrl) {
      page.emailCodeReason = 'A creative tag for emails requires a destination URL to be entered.';
      return;
    }
    const assets = _.map(page.editingModel.cToAssets, (a: any, key: string) => ({key: key, asset: a}));
    const imageIds = _.map(_.filter(page.acsWithAssetMatches, (ac: any) => (MediaTypeHelper.getMediaType(ac.mimeTypes) as any).image), (ac: any) => ac.tag);
    const hasImage = !!_.find(assets, (asset: any) => imageIds.indexOf(asset.key) > -1 && _.get(asset, ['asset', 'id']));
    if (!hasImage) {
      page.emailCodeReason = 'A creative tag for emails requires at least one image to be part of your creative.';
    }
  };

  const callForPreview = () => {
    Promise.all([Promise.resolve(mAllLayoutsPromise), Promise.resolve(mLineItemPromise)]).then(() => getAdvertiserForPreview()).then((previewAdvertiser: any) => {
      syncEditingModel();
      updateEmailCode();

      const previewResults = (failureReason: string | null, previewData?: any) => {
        // Defer — selPreview flips [hidden]="!selPreview" true→false mid-CD (NG0100).
        editPhase.viewWrite(() => {
          page.selPreviewFail = failureReason;
          page.selPreview = previewData;
          const selLayoutType = _.get(page.editingModel, ['layout', 'type'], 'unspecified');
          const key = page.defaultLayoutTypes.indexOf(selLayoutType) > -1 && selLayoutType !== 'HTML' ? 'style1' : 'style2';
          if (previewData && page.selPreviewTrigger[key].previewCreative) {
            page.selPreviewTrigger[key].previewCreative(previewData);
          }
        });
      };

      if (!page.editingModel.layout || !page.editingModel.layout.id) {
        previewResults('A valid layout needs to be selected before a creative can be previewed.');
        return;
      }

      const adServingWarnings = _.find(page.editingModel.layout.validationWarnings, (v: any) => v.preventsAdServing);
      if (adServingWarnings) {
        previewResults('The select layout is invalid and cannot be used to preview a creative.');
        return;
      }

      const creativeAssets = _.get(page.editingModel, ['assets'], [])
        .concat(_.get(page.editingModel, ['libraryCreativeAssets'], []));
      const availableAssets = _.map(creativeAssets, (asset: any) => _.omit(asset, ['matchingAcs', 'assetComponents']));
      if (availableAssets.length === 0 && _.filter(page.editingModel.layout.assetComponents, ['required', 'MANDATORY']).length > 0) {
        previewResults('Mandatory assets must be made available before a creative can be previewed.');
        return;
      }

      const assets = _.filter(availableAssets, (asset: any) => {
        const assetIds = _.keyBy(_.valuesIn(page.editingModel.cToAssets), 'id');
        return assetIds[asset.id];
      });
      const text = _.filter(page.editingModel.cToText, (v: any) => !!v);
      const links = _.filter(page.editingModel.cToUrls, (v: any) => !!v);
      const choices = _.filter(page.editingModel.cToChoices, (v: any) => !!v);
      const mandatoryCompLength = _.filter(_.get(page.editingModel, ['layout', 'layoutComponents'], []), (lc: any) => lc.required === 'MANDATORY').length;
      if (mandatoryCompLength > 0 && assets.length === 0 && text.length === 0 && links.length === 0 && choices.length === 0) {
        previewResults('At least one layout component needs an asset or text assigned to it before a creative can be previewed.');
        return;
      }

      if (page.editingModel.dimensionType === 'FIXED' && (!page.editingModel.width || !page.editingModel.height)) {
        previewResults('The creative must have a width and height before it can be previewed.');
        return;
      }
      if (page.editingModel.dimensionType === 'FIXED_HEIGHT' && !page.editingModel.height) {
        previewResults('The creative must have a height before it can be previewed.');
        return;
      }
      if (page.editingModel.dimensionType === 'FIXED_WIDTH' && !page.editingModel.width) {
        previewResults('The creative must have a width before it can be previewed.');
        return;
      }

      const creativeModel = _.cloneDeep(page.editingModel);
      CreativeHelper.doLayoutParams(page, creativeModel);
      previewResults(null, {
        layoutId: page.editingModel.layout.id,
        creative: creativeModel,
        lineItem: page.lineItem,
        advertiser: previewAdvertiser,
        election: _.get(page.lineItem, 'election') || _.get(page.editingModel, ['lineItem', 'election']) || _.get(page.lineItemInfo, 'election') || _.get(page.editingModel, 'election'),
        // for now the preview api in the ad server expects the video param even for vast audio
        video: requiresVideoPreviewParam(page.editingModel)
      });
    });
  };

  const saveOverridableTargeting = () => {
    page.editingModel.overridableTargeting = [];
    _.forEach(page.tOverrides, (val: any, key: string) => {
      if (val) {
        page.editingModel.overridableTargeting.push(key);
      }
    });
  };

  const overridableTargetingForCtrl = () => {
    page.tOverrides = {};
    page.tOverridesTypes = [];
    _.forEach(ADN_TARGETING_TYPES, (val: any) => {
      const ot = _.find(page.editingModel.overridableTargeting, (tt: string) => tt === val.apiType);
      if (ot) {
        page.tOverridesTypes.push(val.id);
      }
      page.tOverrides[val.apiType] = !!ot;
    });
  };

  const checkShowGeneralVariables = () => {
    page.patchCreativeViewState({
      showGeneralVariables: _.get(page, ['editingModel', 'layout', 'layoutType']) === 'THIRD_PARTY' || !!_.find(page.allComponents || [], (c: any) => c.type === 'TEXT' && c.textType === 'CODE')
    });
  };

  const afterInitAfterSave = (rematchAssets = true) => {
    syncEditingModel();
    page.editingModel.layoutParameters = page.editingModel.layoutParameters || {};
    page.editingModel.cToAssets = page.editingModel.cToAssets || {};
    page.editingModel.cToText = page.editingModel.cToText || {};
    page.editingModel.cToUrls = page.editingModel.cToUrls || {};
    page.editingModel.cToChoices = page.editingModel.cToChoices || {};
    // Save response replaces layout with a stub {id}; restore assetComponents etc. before CD.
    const layoutId = _.get(page.editingModel, ['layout', 'id']);
    const layoutFromRef = layoutId && page.layoutRef[layoutId];
    if (layoutFromRef) {
      page.editingModel.layout = layoutFromRef;
    }
    page.hasLibraryCreative = !!_.get(page.editingModel.libraryCreative, ['id'], '');
    page.lcOverrides = {};
    _.forEach(page.editingModel.overridableLayoutParameters, (key: string) => {
      page.lcOverrides[key] = true;
    });
    overridableTargetingForCtrl();
    page.dimensionOverrides = _.indexOf(page.editingModel.overrideFields, 'width') > -1 || _.indexOf(page.editingModel.overrideFields, 'height') > -1 || _.indexOf(page.editingModel.overrideFields, 'dimensionType') > -1;
    page.savedTeams = _.clone(page.editingModel.teams);
    page.editingModel.renderOption = page.editingModel.renderOption || null;

    page.advertiserMismatch = findCreativeAdvertiserMismatch(page.editingModel.validationWarnings);

    Promise.resolve(mAllLayoutsPromise).then(() => {
      const hydratedLayout = page.layoutRef[_.get(page.editingModel, ['layout', 'id'])];
      if (hydratedLayout) {
        page.editingModel.layout = hydratedLayout;
      }
      const productId = _.get(page.editingModel, ['layoutParameters', 'retailProductId']);
      const merchantId = _.get(page.editingModel, ['layoutParameters', 'merchantId']);
      if (hydratedLayout && hydratedLayout.detectedCategory === 'RETAIL' && productId && merchantId) {
        hydrateSelectedRetailProduct(productId, merchantId);
        const notRetailComponents = _.filter(page.layoutRef[page.editingModel.layout.id].layoutComponents, (lc: any) => !page.retailProductFields[lc.tag]);
        page.isPureRetailCreative = page.layoutRef[page.editingModel.layout.id].layoutType === 'RETAIL' && !notRetailComponents.length;
      }
      if (rematchAssets) {
        return matchConstraintsAndAssets();
      }
      return undefined;
    }).then(() => {
      checkShowGeneralVariables();
    }).catch((error: any) => {
      Rollbar.error('Creative model rehydration problem', error);
    });

    Promise.resolve(CreativeHelper.deriveFee(mLineItemPromise, page, page.editingModel.creativeFormatFee).then((fee: any) => {
      page.editingModel.simplifiedCreativeFormatFee = fee;
    }));

    if (!page.isCreative && page.isPureMarketplace) {
      Promise.resolve(teamQuery).then(() => {
        page.editingModel.singularTeam = page.editingModel.teams[0];
        if (!page.editingModel.singularTeam && page.allTeams.length === 1) {
          page.editingModel.singularTeam = _.get(page, ['allTeams', 0, 'id']);
        }
      });
    }
    checkShowGeneralVariables();
  };

  const afterInit = () => {
    editPhase.wait('init');
    syncEditingModel();
    page.editingModel = {...page.editingModel};
    page.impTrackingUrls = _.map(page.editingModel.impressionTrackingUrls, (url: string) => ({trackingUrl: url}));

    // for an AX_PUBLISHER viewing a creative, some parts of the creative will be suppressed
    const axPublisherPermission = page.editingModel.permission === 'AX_PUBLISHER';
    page.readOnlyAxPublisher = axPublisherPermission && !page.editingModel.targeting;
    page.showTargeting = !axPublisherPermission || page.editingModel.targeting;
    page.showExternalAdUnits = !axPublisherPermission || page.editingModel.externalAdUnits;
    page.showCpm = !axPublisherPermission || page.editingModel.cpm;
    page.blockSubmit = readOnly || page.blockSubmit || page.readOnlyAxPublisher;

    page.titleKey = model ? 'creative.edit' : 'creative.add';
    page.editingModel.targeting = page.editingModel.targeting || {};
    page.editingModel.cToAssets = page.editingModel.cToAssets || {};
    page.editingModel.userState = page.editingModel.userState || 'APPROVED';
    page.editingModel.type = page.editingModel.type || 'INTERNAL';
    page.htmlAcWithAssetMatches = {};
    page.selPreviewTrigger = {style1: {}, style2: {}};
    page.editingModel.teams = _.map(page.editingModel.teams, 'id');

    page.patchCreativeViewState({isLockedDown: false, isCreativeContentLocked: false});
    page.showStats = !axPublisherPermission;

    page.editingModel.dimensionType = page.editingModel.dimensionType || 'FIXED';

    if (!_.get(lineItemInfo, ['id']) && !_.get(page.editingModel, ['lineItem', 'id']) && !_.get(page.editingModel, ['creativeSet', 'id'])) {
      page.orphan = true;
    }

    const advertiser = advertiserFromNavigation(navigationHistory, routeParams);
    if (advertiser) {
      page.editingModel.advertiser = advertiser;
      page.advertiserSpecified = true;
    }
    if (page.editingModel.advertiser && page.orphan) {
      page.advertiserSpecified = true;
    }

    if (_.get(page.editingModel, ['lineItem', 'id'])) {
      getLineItemInfo();
      mInitPromises.push(mLineItemPromise);
    }

    if (!mAllLayoutsPromise) {
      mAllLayoutsPromise = CreativeHelper.getLayouts(page, defaultLayoutType, mLineItemPromise);
    }
    mInitPromises.push(mAllLayoutsPromise);
    if (page.editingModel.id) {
      page.models = [page.editingModel];
      Promise.resolve(mAllLayoutsPromise).then(() => {
        if (page.editingModel.layout) {
          const hydratedLayout = _.find(page.layouts, ['id', page.editingModel.layout.id]);
          if (hydratedLayout) {
            page.editingModel.layout = hydratedLayout;
          }
        }
      });
    }
    Promise.all(mInitPromises).then(() => {
      return Promise.resolve(matchConstraintsAndAssets()).then(() => {
        delayedPreview();
        checkShowGeneralVariables();
      });
    }).then(() => {
      editPhase.done('init');
    }, () => {
      editPhase.done('init');
    });

    if (page.externalAdUnitsHook.initExtAdUnits) {
      page.externalAdUnitsHook.initExtAdUnits();
    }
    afterInitAfterSave(false);
  };

  const afterSave = (creative: any) => {
    const suppressLineItemTransition = suppressLineItemTransitionOnce;
    suppressLineItemTransitionOnce = false;

    syncEditingModel();
    page.targetingResult = {data: false};
    page.editingModel.targeting = creative.targeting || {};
    page.hasLibraryCreative = !!_.get(creative.libraryCreative, ['id'], '');

    page.teamsWarning = (_.get(page.editingModel, 'teams') || []).length !== (_.get(creative, 'teams') || []).length;
    _.set(page.editingModel, 'teams', _.map(_.get(creative, 'teams') || [], 'id'));

    if (_.isFunction(page.eventHook.onCreativeSave)) {
      page.eventHook.onCreativeSave();
    }
    if (page.toLineItem && !suppressLineItemTransition && _.get(creative, ['lineItem', 'id'])) {
      $state.go('app.adv.line-items.line-item', {id: creative.lineItem.id});
      return;
    }
    if (page.toCreativeSet && _.get(creative, ['creativeSet', 'id'])) {
      $state.go('app.adv.creative-sets.creative-set', {id: creative.creativeSet.id});
      return;
    }
    if (_.get(page.editingModel, ['lineItem', 'id']) && page.orphan) {
      getLineItemInfo();
      page.postOrphan = true;
    }
    if (page.externalAdUnitsHook.initExtAdUnits) {
      page.externalAdUnitsHook.initExtAdUnits();
    }
  };

  page.lineItemInfo = lineItemInfo;
  page.toLineItem = !!lineItemInfo.fromLineItem;
  page.toCreativeSet = !!lineItemInfo.fromCreativeSet;

  page.externalAdUnitsHook = {
    editsMade: () => page.editsMade(),
    get model() {
      return page.editingModel;
    }
  };

  page.cancelShareOfVoice = () => {
    page.editingModel.sponsorshipPercentage = undefined;
    page.editsMade();
  };

  page.breakLink = () => {
    syncEditingModel();
    const libCreative = _.cloneDeep(page.editingModel);
    libCreative.libraryCreative = null;
    editPhase.viewWrite(() => {
      page.setExternalPending(true);
      page.isDelinking = true;
    });
    firstValueFrom(creativeApi.savePartial(libCreative, 'libraryCreative')).then((creative: any) => {
      page.editingModel.libraryCreative = creative.libraryCreative;
      page.applyBehaviorSave({...page.editingModel, ...creative});
    }).finally(() => {
      editPhase.viewWrite(() => {
        page.setExternalPending(false);
        page.isDelinking = false;
      });
    });
  };

  page.createRetailCreative = () => {
    page.editsMade();
    const merchantGuide = localNetworkInfo.getMerchantGuide();
    creativeRetailProductModalService.open({
      selRetailProduct: page.selRetailProduct,
      onSelect: (product: any) => {
        const merchantLink = _.get(merchantGuide, [product.merchantId, 'link']);
        editPhase.viewWrite(() => {
          page.setExternalPending(true);
        });
        return ensureRetailCreativeIsSaved().then((savedCreative: any) => {
          const lineItemId = _.get(savedCreative, ['lineItem', 'id']) || _.get(page.editingModel, ['lineItem', 'id']);
          const creativeId = _.get(savedCreative, 'id') || _.get(page.editingModel, 'id') || '';
          return firstValueFrom(creativeApi.retailAd(
            lineItemId,
            creativeId,
            _.get(page.editingModel, ['layout', 'id']),
            merchantLink,
            product.itemId
          ));
        })
          .then((creative: any) => firstValueFrom(creativeApi.hydrate(creative)))
          .then((creative: any) => {
            applyRetailCreativeUpdate(creative);
            return hydrateSelectedRetailProduct(product.itemId, product.merchantId);
          })
          .finally(() => {
            editPhase.viewWrite(() => {
              page.setExternalPending(false);
            });
          });
      }
    });
  };

  page.assetAssignmentChanged = (tag: string) => {
    syncEditingModel();
    const selectedAsset = _.get(page.editingModel, 'cToAssets.' + tag);
    if (selectedAsset && MediaTypeHelper.isMediaType(selectedAsset.mimeType, MEDIA_TYPE.html) && page.primaryHtmlAsset) {
      page.primaryHtmlAsset = selectedAsset;
      checkHtmlUrls();
    }
    updateCreativeDims();
    delayedPreview();
    if (selectedAsset) {
      updateDefaultName(selectedAsset);
    }
    if (_.isFunction(page.eventHook.onAssetUpdate)) {
      page.eventHook.onAssetUpdate();
    }
  };

  page.selectTeams = () => {
    page.editsMade();
    page.editingModel.teams = _.map(page.allTeams, 'id');
  };

  page.deselectTeams = () => {
    page.editsMade();
    page.editingModel.teams = [];
  };

  page.selectLayout = (layout?: any) => {
    syncEditingModel();
    if (layout) {
      page.editingModel.layout = layout;
    }
    page.editingModel.cToAssets = {};
    // ngModelChange can fire with null when the layout select is cleared.
    if (!page.editingModel.layout) {
      page.editingModel.defaultLayoutWidth = null;
      page.editingModel.defaultLayoutHeight = null;
      page.isFullPage = false;
      return;
    }
    if (page.editingModel.defaultLayoutWidth === page.editingModel.width && page.editingModel.defaultLayoutHeight === page.editingModel.height) {
      page.editingModel.width = page.editingModel.layout.defaultWidth;
      page.editingModel.height = page.editingModel.layout.defaultHeight;
    }
    page.editingModel.width = page.editingModel.width || page.editingModel.layout.defaultWidth;
    page.editingModel.height = page.editingModel.height || page.editingModel.layout.defaultHeight;
    page.editingModel.defaultLayoutWidth = page.editingModel.layout.defaultWidth;
    page.editingModel.defaultLayoutHeight = page.editingModel.layout.defaultHeight;
    Promise.resolve(matchConstraintsAndAssets(true)).then(() => {
      checkShowGeneralVariables();
      callForPreview();
    });
  };

  page.addImpressionTrackingUrl = () => {
    page.impTrackingUrls = page.impTrackingUrls || [];
    page.impTrackingUrls.push({trackingUrl: null});
  };

  page.eventHook = {
    onUpdate: (uploadResponse?: any) => {
      page.editsMade();
      return Promise.resolve(matchConstraintsAndAssets(true)).then(() => {
        syncEditingModel();
        if (CreativeHelper.updateCreativeDimsFromVideoUpload(page, uploadResponse)) {
          delayedPreview();
        }
        editPhase.viewWrite(() => undefined);
      });
    },
    assignAsset: (asset: any, tag: string) => {
      page.editsMade();
      page.editingModel.cToAssets[tag] = asset;
      updateCreativeDims();
      delayedPreview();
      updateDefaultName(asset);
    }
  };

  page.componentValueUpdated = () => {
    page.editsMade();
    delayedPreview();
  };

  page.dimensionsChange = () => {
    delayedPreview();
  };

  page.editTarget = () => {
    page.targetingResult = {data: false};
    const productType = _.get(page.editingModel, ['axProduct', 'type']);
    const lineItemType = (_.get(marketplaceProduct, ['type']) === 'POLITICAL' || productType === 'POLITICAL' || _.get(page.lineItem, ['election', 'id']) || _.get(page.editingModel, ['layout', 'political'])) ? 'POLITICAL' : 'NORMAL';
    const modelLineItem = _.cloneDeep(page.lineItem) || {};
    modelLineItem.creativesForReach = [page.editingModel];
    modelLineItem.fromCreative = true;
    let availableTargets: any = null;
    if (_.isArray(page.optionalTargetTypes) && marketplaceProduct) {
      const targetIds = _.map(_.filter(ADN_TARGETING_TYPES, (tt: any) => page.optionalTargetTypes.indexOf(tt.apiType) > -1), 'id');
      availableTargets = _.pick(ADN_TARGETING_TYPES, targetIds);
    }

    TargetingEditorModalService.open({
      lineItemType: lineItemType,
      modelLineItem: modelLineItem,
      pageType: 'creative',
      reachEnabled: 'CREATIVE',
      targetingMode: null,
      overridableTargeting: page.hasLibraryCreative ? page.tOverrides : null,
      mandatoryTargets: undefined,
      availableTargets: availableTargets,
      targeting: _.cloneDeep(page.editingModel.targeting),
      isLockedDown: page.isLockedDown
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      const fieldsForSaving = ['targeting'];
      if (wc.overrides) {
        page.tOverrides = wc.overrides;
        saveOverridableTargeting();
        fieldsForSaving.push('overridableTargeting');
      }
      _.assign(page.editingModel.targeting, wc.wc);
      _.assign(page.editingModel.overridableTargeting, wc.overrides);
      page.editingModel.targeting = _.cloneDeep(page.editingModel.targeting);
      if (!page.isNew) {
        overridableTargetingForCtrl();
        firstValueFrom(creativeApi.savePartial(page.editingModel, fieldsForSaving)).then((respCreative: any) => {
          editPhase.viewWrite(() => {
            page.targetingResult = {data: true};
            _.set(page.editingModel, 'validationWarnings', respCreative.validationWarnings);
            _.set(page.editingModel, 'targeting', respCreative.targeting);
          });
        }, (err: any) => {
          editPhase.viewWrite(() => {
            page.targetingResult = {
              data: false,
              errors: _.get(err, ['data', 'errors'], [])
            };
          });
          Rollbar.error('Creative targeting problem', err);
        });
      } else {
        page.editsMade();
      }
    }, () => undefined);
  };

  page.extDemandSources = null;
  firstValueFrom(externalDemandSourceApi.listOptions()).then((connPage) => {
    editPhase.viewWrite(() => {
      page.extDemandSources = connPage.results || [];
    });
  }, () => {
    editPhase.viewWrite(() => {
      page.extDemandSources = [];
    });
  });

  page.refreshCodeMirrorVar = false;
  page.refreshCodeMirror = () => {
    clearTimeout(codeMirrorRefreshTimeout);
    codeMirrorRefreshTimeout = setTimeout(() => {
      page.refreshCodeMirrorVar = true;
    }, 100);
  };

  const destroy = () => {
    clearTimeout(timeoutPromise);
    clearTimeout(codeMirrorRefreshTimeout);
  };

  const beforeSubmit = () => {
    syncEditingModel();
    if (page.unreject && page.editingModel.userState === 'REJECTED') {
      page.editingModel.userState = 'APPROVED';
    }
    if (!_.isFinite(page.editingModel.sponsorshipPercentage)) {
      page.editingModel.sponsorshipPercentage = null;
    }
    // remove any empty-string impression-tracking urls.
    page.editingModel.impressionTrackingUrls = _.map(_.filter(page.impTrackingUrls, (url: any) => url.trackingUrl && url.trackingUrl.length > 0), 'trackingUrl');

    _.forEach(_.keysIn(page.editingModel.cToAssets), (t: string) => {
      if (!page.editingModel.cToAssets[t]) {
        delete page.editingModel.cToAssets[t];
      }
    });

    page.editingModel.overridableLayoutParameters = [];
    _.forEach(page.lcOverrides, (val: any, key: string) => {
      if (val) {
        page.editingModel.overridableLayoutParameters.push(key);
      }
    });
    saveOverridableTargeting();
    page.editingModel.overrideFields = [];
    if (page.dimensionOverrides) {
      page.editingModel.overrideFields.push('width', 'height', 'dimensionType');
    }

    if (page.editingModel.dimensionType === 'FLEXIBLE') {
      page.editingModel.width = null;
      page.editingModel.height = null;
    } else if (page.editingModel.dimensionType === 'FIXED_WIDTH') {
      page.editingModel.height = null;
    } else if (page.editingModel.dimensionType === 'FIXED_HEIGHT') {
      page.editingModel.width = null;
    }

    CreativeHelper.doLayoutParams(page);
    if (_.isEmpty(page.editingModel.targeting)) {
      // this is to avoid an API bug
      delete page.editingModel.targeting;
    }
    if (page.primaryHtmlAsset) {
      Asset.update(page.editingModel.id, {htmlUrls: page.primaryHtmlAsset.htmlUrls}, page.primaryHtmlAsset.id);
    }

    if ((_.get(page.editingModel, ['cpm', 'amount']) || 0) <= 0) {
      // This will delete the CPM on the server
      page.editingModel.cpm = null;
    }

    if (page.isPureMarketplace && !page.isCreative) {
      page.editingModel.teams = page.editingModel.singularTeam ? [page.editingModel.singularTeam] : null;
    }

    if (page.externalAdUnitsHook.beforeSubmitExtAdUnit) {
      page.externalAdUnitsHook.beforeSubmitExtAdUnit();
    }
  };

  page.selectDimension = () => {
    if (page.selectedDimension) {
      page.editingModel.width = page.selectedDimension.width;
      page.editingModel.height = page.selectedDimension.height;
      delayedPreview();
    }
  };

  page.filterAssets = (assets: any[]) => {
    return _.filter(assets || [], (a: any) => a.objectState === 'ACTIVE' && (!a.htmlCreativeType || MediaTypeHelper.isMediaType(a.mimeType, MEDIA_TYPE.html)));
  };

  page.translateMediaTypes = (mimeTypes: any) => {
    return formatMediaTypesTranslate(mimeTypes, (key) => translate.instant(key));
  };

  page.mediaTypeLabel = (mimeType: any) => {
    return translate.instant(formatMediaTypeKey(mimeType));
  };

  return {
    afterInit,
    afterInitAfterSave: () => afterInitAfterSave(),
    afterSave,
    beforeSubmit,
    destroy
  };
}
